/**
 * CenterSearchBar — 검색바 (구글식).
 *   variant="floating"(기본): 가운데 하단 플로팅(초기 화면).
 *   variant="top": 상단바 내(검색 후 이동). defaultValue=현재 검색어 프리필.
 *
 *   구성: [input] [상품정보] [이미지검색] [X] | [검색]
 *        + 하단 드롭다운: 최근 검색 리스트("사용자가 검색한 리스트").
 *   좌측 LeftAiPanel 검색과 동일 동작(AuditSearchContext.pendingSearch).
 *   2026-09-03: PROVE/관세율표 모드 선택을 뺐다. 검색은 항상 관세율표 lookup(LLM 없는 사전 점검)이고,
 *   PROVE는 결과 화면의 `PROVE 실행` 버튼·사전 점검 카드에서 시작한다. 이미지 인식만 명시적으로 PROVE에 연결한다.
 *   좁은 화면(≤640px)에서는 상품 정보가 아이콘+개수 배지로 줄고 자리표시 문구가 짧아진다.
 */
import React, { useState, useEffect, useRef } from "react";
import { ImagePlus, Search, SlidersHorizontal, X } from "lucide-react";
import { useAuditSearch } from "./AuditSearchContext";
import { formatHsInput } from "./LeftAiPanel";
import { routeRecognition, isAcceptableImage } from "./imageRecognize";
import { removeRecentReview, saveRecentReview } from "./classificationWorkspace";
import { useNarrowViewport } from "./useNarrowViewport";
import "./theme.css";

// 검색바 제출은 항상 관세율표 lookup. PROVE는 결과 화면에서 명시적으로 시작한다.
const LOOKUP_MODE = "auto";

const RECENT_KEY = "loginhs_recent_searches";
const EMPTY_PRODUCT_DETAILS = Object.freeze({
  material: "",
  intendedUse: "",
  construction: "",
  articleRole: "",
});
const detailInputStyle = {
  width: "100%",
  minWidth: 0,
  height: 34,
  boxSizing: "border-box",
  border: "1px solid var(--audit-border-strong, #cdd1d8)",
  borderRadius: 8,
  outline: "none",
  background: "var(--audit-bg)",
  color: "var(--audit-text)",
  padding: "6px 9px",
  fontFamily: "inherit",
  fontSize: 12.5,
};
function loadRecents() {
  try {
    const a = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(a) ? a.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function saveRecent(q) {
  try {
    saveRecentReview(q);
  } catch {
    /* localStorage 불가 무시 */
  }
}
function dropRecent(q) {
  try {
    removeRecentReview(q);
  } catch {
    /* noop */
  }
}

export default function CenterSearchBar({ variant = "floating", defaultValue = "" }) {
  const { setPendingSearch } = useAuditSearch();
  const [q, setQ] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const narrow = useNarrowViewport();
  const [recents, setRecents] = useState(loadRecents);
  const [hi, setHi] = useState(-1); // 키보드 하이라이트 인덱스(-1=입력값)
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const compact = variant === "top";
  const fileRef = useRef(null);
  const [imgBusy, setImgBusy] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [productDetails, setProductDetails] = useState(() => ({ ...EMPTY_PRODUCT_DETAILS }));
  const [imgNotice, setImgNotice] = useState(null); // {kind:"chip"|"invoice"|"hint", text}

  useEffect(() => {
    const reset = () => {
      setQ("");
      setOpen(false);
      setDetailsOpen(false);
      setProductDetails({ ...EMPTY_PRODUCT_DETAILS });
      setHi(-1);
      setImgNotice(null);
    };
    window.addEventListener("loginhs:new-review", reset);
    return () => window.removeEventListener("loginhs:new-review", reset);
  }, []);

  useEffect(() => {
    const applyConfirmedDetails = (event) => {
      const details = event?.detail || {};
      setProductDetails({
        material: String(details.material || "").trim(),
        intendedUse: String(details.intendedUse || details.intended_use || "").trim(),
        construction: String(details.construction || "").trim(),
        articleRole: ["complete_machine", "part", "accessory"].includes(details.articleRole || details.article_role)
          ? (details.articleRole || details.article_role)
          : "",
      });
    };
    window.addEventListener("loginhs:product-details-applied", applyConfirmedDetails);
    return () => window.removeEventListener("loginhs:product-details-applied", applyConfirmedDetails);
  }, []);

  const submit = (query) => {
    const val = String(query == null ? q : query).trim();
    if (!val) return;
    // 같은 검색어를 다시 제출하면 이전 PROVE 결과를 재사용하지 않고
    // 사용자가 요청한 새 분류 사이클을 시작한다.
    try {
      sessionStorage.removeItem(`loginhs:ai-thread:${val}`);
      sessionStorage.removeItem(`loginhs:search-refinement:${val}`);
    } catch { /* private browsing */ }
    saveRecent(val);
    setRecents(loadRecents());
    setOpen(false);
    setDetailsOpen(false);
    setHi(-1);
    setQ(val);
    setPendingSearch({ q: val, ts: Date.now(), mode: LOOKUP_MODE, productDetails });
  };

  const handleImageFile = async (file) => {
    if (!file) return;
    if (!isAcceptableImage(file)) {
      setImgNotice({ kind: "hint", text: "PNG·JPEG·WebP·GIF, 10MB 이하 이미지만 지원합니다." });
      return;
    }
    setImgBusy(true);
    setImgNotice({ kind: "hint", text: "이미지 인식 중…" });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
      const r = await fetch("/api/onto/image-recognize", {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const data = await r.json().catch(() => null);
      const route = routeRecognition(data);
      if (route.action === "search") {
        setImgNotice({ kind: "chip", text: `이미지 인식: ${route.label}` });
        setQ(route.query);
        setPendingSearch({ q: route.query, ts: Date.now(), mode: "ai", productDetails });
      } else {
        setImgNotice({ kind: route.action === "invoice" ? "invoice" : "hint", text: route.message });
      }
    } catch {
      setImgNotice({ kind: "hint", text: "인식 실패 — 잠시 후 다시 시도하거나 키워드로 검색해 주세요." });
    } finally {
      setImgBusy(false);
    }
  };

  const onPasteImage = (e) => {
    const item = [...(e.clipboardData?.items || [])].find((it) => it.type.startsWith("image/"));
    if (item) { e.preventDefault(); handleImageFile(item.getAsFile()); }
  };
  const onDropImage = (e) => {
    const f = e.dataTransfer?.files?.[0];
    if (f && f.type.startsWith("image/")) { e.preventDefault(); handleImageFile(f); }
  };

  // 바깥 클릭 → 최근 검색·상품정보 패널 닫기
  useEffect(() => {
    if (!open && !detailsOpen) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setDetailsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, detailsOpen]);

  const qTrim = q.trim().toLowerCase();
  const suggestions = recents
    .filter((r) => !qTrim || r.toLowerCase().includes(qTrim))
    .slice(0, 8);
  const detailCount = Object.values(productDetails).filter((value) => String(value || "").trim()).length;

  const updateProductDetail = (key, value) => {
    setProductDetails((current) => ({ ...current, [key]: value }));
  };

  const barChildren = (
    <>
      <Search size={17} strokeWidth={2.2} aria-hidden="true" style={{ flexShrink: 0, color: "var(--audit-text-faint)" }} />

      <input
        ref={inputRef}
        value={q}
        onChange={(e) => {
          setQ(formatHsInput(e.target.value));
          setDetailsOpen(false);
          setOpen(true);
          setHi(-1);
        }}
        onFocus={() => {
          setDetailsOpen(false);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          const n = suggestions.length;
          if (e.key === "ArrowDown" && open && n > 0) {
            e.preventDefault();
            setHi((i) => (i + 1) % n);
          } else if (e.key === "ArrowUp" && open && n > 0) {
            e.preventDefault();
            setHi((i) => (i <= 0 ? n - 1 : i - 1));
          } else if (e.key === "Enter") {
            // 하이라이트된 최근검색이 있으면 그것으로, 없으면 입력값으로 검색.
            if (hi >= 0 && hi < n) submit(suggestions[hi]);
            else submit();
          } else if (e.key === "Escape") {
            setOpen(false);
            setHi(-1);
          }
        }}
        onPaste={onPasteImage}
        placeholder={narrow ? "세번·품명 검색" : "세번(2009.4) 또는 키워드(handbag) 검색"}
        style={{
          flex: 1,
          minWidth: 0,
          border: "none",
          outline: "none",
          background: "transparent",
          fontSize: narrow ? 16 : 15,
          color: "var(--audit-text)",
          fontFamily: "inherit",
          marginLeft: 6,
        }}
      />

      <button
        type="button"
        onClick={() => {
          setOpen(false);
          setDetailsOpen((value) => !value);
        }}
        aria-label="상품 정보 추가"
        aria-expanded={detailsOpen}
        title="재질·용도 등 분류에 필요한 상품 정보 추가"
        style={{
          border: detailCount ? "1px solid var(--audit-accent, #2563eb)" : "1px solid var(--audit-border)",
          background: detailCount ? "var(--audit-accent-soft, #eff6ff)" : "transparent",
          color: detailCount ? "var(--audit-accent, #2563eb)" : "var(--audit-text-muted)",
          borderRadius: 999,
          cursor: "pointer",
          padding: narrow ? "6px 7px" : "4px 7px",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          flexShrink: 0,
          fontFamily: "inherit",
          fontSize: 11.5,
          fontWeight: detailCount ? 700 : 600,
          position: "relative",
        }}
      >
        <SlidersHorizontal size={narrow ? 17 : 15} />
        {narrow
          ? (detailCount > 0 && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute", top: -4, right: -4, minWidth: 16, height: 16, padding: "0 4px",
                borderRadius: 999, background: "var(--audit-accent, #2563eb)", color: "#fff",
                fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
            >
              {detailCount}
            </span>
          ))
          : <span>{detailCount ? `상품 정보 ${detailCount}` : "상품 정보"}</span>}
      </button>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        style={{ display: "none" }}
        onChange={(e) => { handleImageFile(e.target.files?.[0]); e.target.value = ""; }}
      />
      <button
        type="button"
        onClick={() => fileRef.current && fileRef.current.click()}
        aria-label="이미지로 검색"
        title="이미지로 검색 — 제품 사진을 업로드하거나 붙여넣으세요"
        disabled={imgBusy}
        style={{
          border: "none", background: "transparent", cursor: imgBusy ? "default" : "pointer",
          padding: 4, display: "inline-flex", alignItems: "center",
          color: imgBusy ? "var(--audit-text-faint)" : "var(--audit-text)", flexShrink: 0,
        }}
      >
        <ImagePlus size={19} strokeWidth={2} />
      </button>

      {/* X — 검색어 지우기 */}
      {q && (
        <button
          onClick={() => {
            setQ("");
            setOpen(true);
            if (inputRef.current) inputRef.current.focus();
          }}
          title="지우기"
          style={{
            flexShrink: 0,
            border: "none",
            background: "transparent",
            color: "var(--audit-text-muted)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            padding: 4,
            borderRadius: 999,
          }}
        >
          <X size={17} />
        </button>
      )}

      {/* 구분선 */}
      <span
        style={{
          width: 1,
          alignSelf: "stretch",
          margin: "4px 4px",
          background: "var(--audit-border)",
          flexShrink: 0,
        }}
      />

      {/* 🔍 검색 = 맨 오른쪽 (구글식 회색) */}
      <button
        onClick={() => submit()}
        title="검색"
        style={{
          flexShrink: 0,
          border: "none",
          background: "transparent",
          color: "var(--audit-text)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 6,
          borderRadius: 999,
        }}
      >
        <Search size={20} strokeWidth={2.4} />
      </button>
    </>
  );

  const productDetailsPanel = detailsOpen && (
    <div
      role="dialog"
      aria-label="상품 정보 입력"
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        left: 0,
        right: 0,
        zIndex: 70,
        padding: "14px 16px",
        border: "1px solid var(--audit-border-strong, #cdd1d8)",
        borderRadius: 14,
        background: "var(--audit-bg-elevated, var(--audit-bg))",
        boxShadow: "0 14px 42px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 11 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: "var(--audit-text)" }}>상품 정보 추가</div>
          <div style={{ marginTop: 3, fontSize: 11.5, lineHeight: 1.45, color: "var(--audit-text-muted)" }}>
            아는 항목만 입력하세요. 검색 후 결정트리가 부족한 사실만 이어서 확인합니다.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDetailsOpen(false)}
          aria-label="상품 정보 닫기"
          style={{ border: "none", background: "transparent", color: "var(--audit-text-muted)", cursor: "pointer", padding: 2 }}
        >
          <X size={17} />
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "10px 12px" }}>
        <label style={{ display: "grid", gap: 5, fontSize: 12, fontWeight: 700, color: "var(--audit-text)" }}>
          재질·구성
          <input
            value={productDetails.material}
            onChange={(e) => updateProductDetail("material", e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="예: 스테인리스강, 면 80%·폴리에스터 20%"
            style={detailInputStyle}
          />
        </label>
        <label style={{ display: "grid", gap: 5, fontSize: 12, fontWeight: 700, color: "var(--audit-text)" }}>
          용도·사용처
          <input
            value={productDetails.intendedUse}
            onChange={(e) => updateProductDetail("intendedUse", e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="예: 자동차 배선 연결, 식품 여과"
            style={detailInputStyle}
          />
        </label>
        <label style={{ display: "grid", gap: 5, fontSize: 12, fontWeight: 700, color: "var(--audit-text)" }}>
          형태·작동방식
          <input
            value={productDetails.construction}
            onChange={(e) => updateProductDetail("construction", e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="예: 커넥터 부착 케이블, 편직물"
            style={detailInputStyle}
          />
        </label>
        <label style={{ display: "grid", gap: 5, fontSize: 12, fontWeight: 700, color: "var(--audit-text)" }}>
          품목 상태
          <select
            value={productDetails.articleRole}
            onChange={(e) => updateProductDetail("articleRole", e.target.value)}
            style={{ ...detailInputStyle, cursor: "pointer" }}
          >
            <option value="">모름 / 선택 안 함</option>
            <option value="complete_machine">완성 기계·기기</option>
            <option value="part">부분품</option>
            <option value="accessory">부속품</option>
          </select>
        </label>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 12 }}>
        <button
          type="button"
          onClick={() => setProductDetails({ ...EMPTY_PRODUCT_DETAILS })}
          style={{ border: "none", background: "transparent", color: "var(--audit-text-muted)", cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}
        >
          입력 지우기
        </button>
        <button
          type="button"
          onClick={() => setDetailsOpen(false)}
          style={{ border: "1px solid var(--audit-border-strong, #cdd1d8)", borderRadius: 8, background: "var(--audit-bg)", color: "var(--audit-text)", cursor: "pointer", padding: "6px 12px", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700 }}
        >
          적용
        </button>
      </div>
    </div>
  );

  // 하단 드롭다운 — 최근 검색
  const dropdown = open && suggestions.length > 0 && (
    <div
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        left: 0,
        right: 0,
        zIndex: 60,
        background: "var(--audit-bg-elevated, var(--audit-bg))",
        border: "1px solid var(--audit-border-strong, #cdd1d8)",
        borderRadius: 14,
        boxShadow: "0 12px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)",
        padding: "6px 0",
        maxHeight: 340,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: "var(--audit-text-muted)",
          padding: "3px 16px 5px",
          letterSpacing: "0.02em",
        }}
      >
        최근 검색
      </div>
      {suggestions.map((s, idx) => (
        <div
          key={s}
          onMouseDown={(e) => {
            e.preventDefault();
            submit(s);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 14px",
            cursor: "pointer",
            fontSize: 14,
            // 키보드 하이라이트(↑↓) 반영
            background: idx === hi ? "var(--audit-bg-subtle)" : "transparent",
          }}
          onMouseEnter={(e) => {
            setHi(idx);
            e.currentTarget.style.background = "var(--audit-bg-subtle)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = idx === hi ? "var(--audit-bg-subtle)" : "transparent";
          }}
        >
          <Search size={15} style={{ color: "var(--audit-text-muted)", flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {s}
          </span>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dropRecent(s);
              setRecents(loadRecents());
            }}
            title="기록에서 삭제"
            style={{
              border: "none",
              background: "transparent",
              color: "var(--audit-text-muted)",
              cursor: "pointer",
              fontSize: 12,
              padding: "2px 6px",
              flexShrink: 0,
            }}
          >
            삭제
          </button>
        </div>
      ))}
    </div>
  );

  // 상단바 컴팩트(인라인 pill) — 편안한 크기
  if (compact) {
    return (
      <div
        ref={wrapRef}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropImage}
        style={{ position: "relative", zIndex: 100, flex: 1, minWidth: 0, maxWidth: 720 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "6px 8px 6px 10px",
            borderRadius: 999,
            border: "1px solid var(--audit-border-strong, #cdd1d8)",
            background: "var(--audit-bg-elevated, var(--audit-bg))",
          }}
        >
          {barChildren}
        </div>
        {productDetailsPanel}
        {dropdown}
        {imgNotice && (
          <div
            style={{
              marginTop: 6, fontSize: 12.5, lineHeight: 1.5, padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid var(--audit-border)",
              background: imgNotice.kind === "invoice" ? "#eff6ff" : "var(--audit-bg-subtle)",
              color: imgNotice.kind === "chip" ? "var(--audit-text)" : "var(--audit-text-muted)",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <span style={{ flex: 1 }}>{imgNotice.text}</span>
            <button
              type="button"
              onClick={() => setImgNotice(null)}
              style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--audit-text-faint)" }}
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    );
  }

  // 홈 대시보드 — 인라인(플로팅/스티키 아님). HomeDashboard 가 폭·정렬 담당.
  if (variant === "home") {
    return (
      <div
        ref={wrapRef}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropImage}
        style={{ position: "relative", width: "100%" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "7px 9px 7px 12px",
            borderRadius: 999,
            border: "1px solid var(--audit-border-strong, #cdd1d8)",
            background: "var(--audit-bg-elevated, var(--audit-bg))",
            boxShadow: "0 6px 20px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.04)",
          }}
        >
          {barChildren}
        </div>
        {productDetailsPanel}
        {dropdown}
        {imgNotice && (
          <div
            style={{
              marginTop: 6, fontSize: 12.5, lineHeight: 1.5, padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid var(--audit-border)",
              background: imgNotice.kind === "invoice" ? "#eff6ff" : "var(--audit-bg-subtle)",
              color: imgNotice.kind === "chip" ? "var(--audit-text)" : "var(--audit-text-muted)",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <span style={{ flex: 1 }}>{imgNotice.text}</span>
            <button
              type="button"
              onClick={() => setImgNotice(null)}
              style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--audit-text-faint)" }}
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    );
  }

  // 기본 — 가운데 하단 플로팅
  return (
    <div
      style={{
        position: "sticky",
        bottom: "33vh",
        zIndex: 20,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
        marginTop: 12,
      }}
    >
      <div
        ref={wrapRef}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropImage}
        style={{ position: "relative", pointerEvents: "auto", width: "min(720px, 92%)" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "6px 8px 6px 10px",
            borderRadius: 999,
            border: "1px solid var(--audit-border-strong, #cdd1d8)",
            background: "var(--audit-bg-elevated, var(--audit-bg))",
            boxShadow:
              "0 14px 44px rgba(0,0,0,0.22), 0 3px 10px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)",
          }}
        >
          {barChildren}
        </div>
        {productDetailsPanel}
        {dropdown}
        {imgNotice && (
          <div
            style={{
              marginTop: 6, fontSize: 12.5, lineHeight: 1.5, padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid var(--audit-border)",
              background: imgNotice.kind === "invoice" ? "#eff6ff" : "var(--audit-bg-subtle)",
              color: imgNotice.kind === "chip" ? "var(--audit-text)" : "var(--audit-text-muted)",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <span style={{ flex: 1 }}>{imgNotice.text}</span>
            <button
              type="button"
              onClick={() => setImgNotice(null)}
              style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--audit-text-faint)" }}
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
